import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Globe, Menu, X } from 'lucide-react';
import FooterSocialLinks from './FooterSocialLinks';
import TermsAcceptanceModal from './TermsAcceptanceModal';
import { useLanguage } from '../LanguageContext';

const NAV_LINKS = [
  { to: '/#about', key: 'about' },
  { to: '/#sectors', key: 'causes' },
  { to: '/#resources', key: 'resources' },
  { to: '/#totals', key: 'totals' },
  { to: '/#contribute', key: 'contribute' },
  { to: '/#donate', key: 'donate' },
  { to: '/contact', key: 'contact' },
];

const LANGUAGES = [
  { code: 'en', labelKey: 'langEn' },
  { code: 'so', labelKey: 'langSo' },
  { code: 'ar', labelKey: 'langAr' },
];

function NavLinks({ t, onNavigate, className = '' }) {
  return (
    <ul className={`navbar__links ${className}`.trim()}>
      {NAV_LINKS.map(({ to, key }) => (
        <li key={key}>
          <Link to={to} onClick={onNavigate}>
            {t(`nav.${key}`)}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function LanguageMenu({ lang, setLang, t }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  function selectLanguage(code) {
    setLang(code);
    setOpen(false);
  }

  return (
    <div className="navbar__lang-menu" ref={rootRef}>
      <button
        type="button"
        className={`navbar__lang-trigger${open ? ' navbar__lang-trigger--open' : ''}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('nav.langLabel')}
        onClick={() => setOpen((value) => !value)}
      >
        <Globe size={20} strokeWidth={2} aria-hidden="true" />
        <span className="navbar__lang-code" aria-hidden="true">
          {lang.toUpperCase()}
        </span>
      </button>

      {open ? (
        <ul className="navbar__lang-dropdown" role="listbox" aria-label={t('nav.langLabel')}>
          {LANGUAGES.map(({ code, labelKey }) => (
            <li key={code} role="option" aria-selected={lang === code}>
              <button
                type="button"
                className={`navbar__lang-option${lang === code ? ' is-active' : ''}`}
                onClick={() => selectLanguage(code)}
              >
                <span className="navbar__lang-option-code">{code.toUpperCase()}</span>
                <span className="navbar__lang-option-label">{t(`nav.${labelKey}`)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function Layout() {
  const { lang, setLang, t } = useLanguage();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  function closeMenu() {
    setMenuOpen(false);
  }

  return (
    <div className="app" id="top">
      <nav
        className={`navbar${menuOpen ? ' navbar--open' : ''}`}
        aria-label="Main"
      >
        <div className="navbar__inner">
          <Link className="navbar__brand navbar__brand--logo" to="/" onClick={closeMenu}>
            <img
              className="navbar__logo"
              src={`${process.env.PUBLIC_URL}/logo-maalin-iyo-habeen.png`}
              alt="Maalin iyo Habeen"
            />
          </Link>

          <div className="navbar__desktop">
            <NavLinks t={t} />
            <LanguageMenu lang={lang} setLang={setLang} t={t} />
          </div>

          <div className="navbar__actions">
            <LanguageMenu lang={lang} setLang={setLang} t={t} />
            <button
              type="button"
              className="navbar__toggle"
              aria-expanded={menuOpen}
              aria-controls="navbar-menu"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="sr-only">
                {menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
              </span>
              {menuOpen ? (
                <X size={22} strokeWidth={2} aria-hidden="true" />
              ) : (
                <Menu size={22} strokeWidth={2} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        <div
          id="navbar-menu"
          className={`navbar__panel${menuOpen ? ' navbar__panel--open' : ''}`}
          aria-hidden={!menuOpen}
        >
          <NavLinks t={t} onNavigate={closeMenu} className="navbar__links--mobile" />
        </div>

        <button
          type="button"
          className={`navbar__backdrop${menuOpen ? ' navbar__backdrop--visible' : ''}`}
          aria-hidden={!menuOpen}
          tabIndex={menuOpen ? 0 : -1}
          onClick={closeMenu}
        />
      </nav>

      <Outlet />

      <footer className="footer">
        <div className="footer__inner">
          <div className="footer__brand">
            <Link to="/" className="footer__logo-link">
              <img
                className="footer__logo"
                src={`${process.env.PUBLIC_URL}/logo-maalin-iyo-habeen.png`}
                alt="Maalin iyo Habeen"
              />
            </Link>
            <p className="footer__about">{t('footer.aboutText')}</p>
            <FooterSocialLinks />
          </div>

          <nav className="footer__col" aria-label={t('footer.menuAria')}>
            <h2 className="footer__heading">{t('footer.menuHeading')}</h2>
            <ul className="footer__list">
              {NAV_LINKS.map(({ to, key }) => (
                <li key={key}>
                  <Link to={to}>{t(`nav.${key}`)}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="footer__col" aria-label={t('footer.legalAria')}>
            <h2 className="footer__heading">{t('footer.legalHeading')}</h2>
            <ul className="footer__list">
              <li>
                <Link to="/terms">{t('footer.terms')}</Link>
              </li>
              <li>
                <Link to="/privacy">{t('footer.privacy')}</Link>
              </li>
            </ul>
          </nav>

          <div className="footer__col footer__col--contact">
            <h2 className="footer__heading">{t('footer.contactHeading')}</h2>
            <ul className="footer__contact-list">
              <li>
                <a
                  className="footer__contact-link"
                  href={`mailto:${t('contact.emailValue')}`}
                >
                  {t('contact.emailValue')}
                </a>
              </li>
              <li>
                <a
                  className="footer__contact-link"
                  href={`tel:${t('contact.phoneValue').replace(/\s/g, '')}`}
                >
                  {t('contact.phoneValue')}
                </a>
              </li>
              <li>
                <span className="footer__contact-text">
                  {t('contact.addressValue')}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="footer__bottom">
          <p className="footer__thanks">{t('footer.thanks')}</p>
          <p className="footer__meta">
            <span className="footer__copyright">
              © {new Date().getFullYear()} Maalin iyo Habeen
            </span>
            <span className="footer__sep-dot" aria-hidden="true">
              ·
            </span>
            <span className="footer__rights">{t('footer.rights')}</span>
          </p>
        </div>
      </footer>

      <TermsAcceptanceModal />
    </div>
  );
}
